import { Camelized, camelizeKeys } from 'humps';

import { components } from '@/types/codegen/apiTypes';

import { useAuthStore } from '../stores/authStore';
import { api } from './api';

// /api/auth/login request
export interface LoginStartRequest {
  email: string;
  password: string;
  otp_channel: 'email' | 'sms';
  last_four_digits?: string; // Optional, required only for sms
}

// /api/auth/login response
export interface LoginStartResponse {
  success: boolean;
  message: string;
  user_id?: string; // Returned on success
}

// /api/auth/login/verify request
export interface LoginVerifyRequest {
  user_id: string;
  code: string;
  otp_channel: 'email' | 'sms';
}

// /api/auth/login/verify response
export interface LoginVerifyResponse {
  access: string;
  refresh: string;
  user_id: string;
  customer_profile_id: string;
}

// --- Other Needed Types ---

// /api/token/refresh request/response
export interface TokenRefreshInput {
  refresh: string;
}
export interface TokenRefreshOutput {
  access: string;
}

// /api/token/verify request
export interface TokenVerifyInput {
  token: string;
}

// /api/auth/me response
export interface UserInfo {
  id: string;
  email: string | null;
  phone: string | null;
  username: string | null;
  is_active: boolean;
  last_login: string | null;
}

// /api/auth/password/change request/response
export interface PasswordChangeInput {
  current_password: string;
  new_password: string;
}
export interface PasswordChangeOutput {
  message: string;
}

// --- authApi Service Definition ---

// Response type for resend confirmation email
export interface ResendConfirmationResponse {
  success: boolean;
  message: string;
}

// Response type for password reset request
export interface PasswordResetResponse {
  message: string;
  error?: string;
}

export const authApi = {
  // Removed legacy token login & old OTP steps

  /**
   * Step 1: Start Login - Validate credentials and initiate OTP.
   * POST /api/auth/login
   */
  loginStart: async (credentials: LoginStartRequest): Promise<LoginStartResponse> => {
    const { data } = await api.post<LoginStartResponse>('/api/auth/login', credentials);
    return data;
  },

  /**
   * Step 2: Verify Login - Verify OTP code and get final JWTs.
   * POST /api/auth/login/verify
   */
  loginVerify: async (verificationData: LoginVerifyRequest): Promise<LoginVerifyResponse> => {
    const { data } = await api.post<LoginVerifyResponse>('/api/auth/login/verify', verificationData);
    return data;
  },

  // Get current user information
  getCurrentUser: async (token: string): Promise<UserInfo> => {
    const { data } = await api.get<UserInfo>('/api/auth/me', {
      headers: { Authorization: `Bearer ${token}` },
    });
    return data;
  },

  logout: () => {
    // Token clearing is handled by the auth store
  },

  refreshToken: async (): Promise<string> => {
    // Get refresh token from Zustand store state
    const refresh = useAuthStore.getState().refreshToken;
    if (!refresh) {
      throw new Error('No refresh token available in auth state');
    }

    const refreshData: TokenRefreshInput = { refresh };
    const { data } = await api.post<TokenRefreshOutput>('/api/token/refresh', refreshData);

    if (data.access) {
      return data.access;
    }

    throw new Error('Failed to refresh token');
  },

  verifyToken: async (token: string): Promise<boolean> => {
    try {
      const verifyData: TokenVerifyInput = { token };
      await api.post('/api/token/verify/', verifyData);
      return true;
    } catch {
      return false;
    }
  },

  /**
   * Create an alias between anonymous ID and authenticated user
   * This should be called after successful login/registration
   */
  createAlias: async (token: string): Promise<boolean> => {
    try {
      // Get anonymousId from Zustand store state
      const anonymousId = useAuthStore.getState().anonymousId;

      // If no anonymousId, there's nothing to alias
      if (!anonymousId) {
        return false;
      }

      // Call the alias endpoint to connect anonymous activity with the logged-in user

      return true;
    } catch (error) {
      console.error('Error creating alias:', error);
      return false;
    }
  },

  // Change password for authenticated user
  changePassword: async (token: string, input: PasswordChangeInput): Promise<PasswordChangeOutput> => {
    if (!token) {
      throw new Error('Authentication token is required to change password.');
    }
    const { data } = await api.post<PasswordChangeOutput>('/api/auth/password/change', input, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return data;
  },

  /**
   * Calls the backend API to confirm email and set password.
   * POST /api/auth/confirm-email
   * @param userId The user's ID.
   * @param token The confirmation token.
   * @param newPassword The new password to set.
   * @returns A promise resolving with the backend response.
   */
  /**
   * Resends the confirmation email for a user.
   * POST /api/auth/resend-confirmation-email
   * @param email The email address to resend the confirmation email for.
   * @returns A promise resolving with the backend response.
   */
  resendConfirmationEmail: async (email: string): Promise<ResendConfirmationResponse> => {
    // Extra validation to ensure email is valid
    if (!email || typeof email !== 'string' || !email.includes('@')) {
      console.error('Invalid email provided:', email);
      return {
        success: false,
        message: 'Invalid email format. Please provide a valid email address.',
      };
    }

    const url = '/api/auth/resend-confirmation-email';
    const payload = {
      email: email.trim(),
    };

    try {
      // The api interceptor will add the API key header for unauthenticated requests
      const { data } = await api.post(url, payload);
      return data;
    } catch (error: any) {
      console.error('Error resending confirmation email:', error);
      console.error('Error details:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message,
      });

      // Handle API errors and transform them to the expected format
      return {
        success: false,
        message:
          error.response?.data?.detail ||
          error.response?.data?.message ||
          'An error occurred while resending the confirmation email. Please try again later.',
      };
    }
  },

  confirmEmailAndSetPassword: async (
    token: string,
    newPassword: string,
    email?: string,
  ): Promise<{ success: boolean; message: string }> => {
    // Extra validation to ensure parameters are valid before making the request
    if (!token || typeof token !== 'string') {
      console.error('Invalid token provided:', token);
      return {
        success: false,
        message: 'Invalid token format. Please check your confirmation link.',
      };
    }

    if (!newPassword || typeof newPassword !== 'string' || newPassword.length < 8) {
      console.error('Invalid password provided, length:', newPassword?.length);
      return {
        success: false,
        message: 'Invalid password. Please ensure your password meets all requirements.',
      };
    }

    // Ensure token is in the standard UUID format (with hyphens)
    let formattedToken = token.trim();
    // Check if this is a valid UUID format but just make sure it's properly formatted
    if (formattedToken && formattedToken.length === 32 && !formattedToken.includes('-')) {
      // Format token by adding hyphens in the UUID positions
      formattedToken = `${formattedToken.slice(0, 8)}-${formattedToken.slice(8, 12)}-${formattedToken.slice(12, 16)}-${formattedToken.slice(16, 20)}-${formattedToken.slice(20)}`;
    }

    const url = '/api/auth/confirm-email'; // Use relative URL, handled by api interceptor
    // Define the payload type to include the optional email property
    const payload: {
      token: string;
      new_password: string;
      email?: string;
    } = {
      token: formattedToken, // Use the properly formatted token
      new_password: newPassword,
    };

    // Email is now required with the new email-based token system
    if (email) {
      payload.email = email.trim();
    } else {
      console.warn(
        'Email not provided for token confirmation. This may cause issues with the new email-based token system.',
      );
    }

    try {
      // The api interceptor will add the API key header for unauthenticated requests
      const { data } = await api.post(url, payload);
      return data;
    } catch (error: any) {
      console.error('Error confirming email:', error);
      console.error('Error details:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message,
      });

      // Handle API errors and transform them to the expected format
      return {
        success: false,
        message:
          error.response?.data?.detail ||
          error.response?.data?.message ||
          'An error occurred during email confirmation. Please try again.',
      };
    }
  },

  getMe: async (): Promise<Camelized<components['schemas']['UserInfoResponse']>> => {
    const { data } = await api.get('/api/auth/me');
    return camelizeKeys(data) as Camelized<components['schemas']['UserInfoResponse']>;
  },

  /**
   * Initiates the password reset process for a user by email.
   * POST /api/auth/password/reset/request
   * @param email The email address to reset password for.
   * @returns A promise resolving with the backend response.
   */
  requestPasswordReset: async (email: string): Promise<PasswordResetResponse> => {
    // Extra validation to ensure email is valid
    if (!email || typeof email !== 'string' || !email.includes('@')) {
      console.error('Invalid email provided:', email);
      return {
        message: 'Invalid email format. Please provide a valid email address.',
        error: 'Invalid email format',
      };
    }

    const url = '/api/auth/password/reset/request';
    const payload = {
      email: email.trim(),
    };

    try {
      // The api interceptor will add the API key header for unauthenticated requests
      const { data } = await api.post<PasswordResetResponse>(url, payload);
      return data;
    } catch (error: any) {
      console.error('Error requesting password reset:', error);
      console.error('Error details:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message,
      });

      const status = error.response?.status;
      const message =
        error.response?.data?.detail ||
        error.response?.data?.message ||
        'An error occurred while requesting password reset. Please try again later.';

      const enrichedError = new Error(message) as Error & { status?: number };
      enrichedError.status = status;
      throw enrichedError;
    }
  },

  /**
   * Confirms the password reset and sets a new password.
   * POST /api/auth/password/reset/confirm
   * @param token The reset token from the email link.
   * @param uidb64 The encoded user ID from the email link.
   * @param newPassword The new password to set.
   * @returns A promise resolving with the backend response.
   */
  resetPassword: async (token: string, uidb64: string, newPassword: string): Promise<PasswordResetResponse> => {
    // Extra validation to ensure parameters are valid
    if (!token || typeof token !== 'string') {
      console.error('Invalid token provided');
      return {
        message: 'Invalid token format. Please check your reset link.',
        error: 'Invalid token',
      };
    }

    if (!uidb64 || typeof uidb64 !== 'string') {
      console.error('Invalid uidb64 provided');
      return {
        message: 'Invalid user identifier. Please check your reset link.',
        error: 'Invalid user identifier',
      };
    }

    if (!newPassword || typeof newPassword !== 'string' || newPassword.length < 8) {
      console.error('Invalid password provided, length:', newPassword?.length);
      return {
        message: 'Invalid password. Please ensure your password meets all requirements.',
        error: 'Invalid password',
      };
    }

    const url = '/api/auth/password/reset/confirm';
    const payload = {
      token: token.trim(),
      uidb64: uidb64.trim(),
      new_password: newPassword,
    };

    try {
      // The api interceptor will add the API key header for unauthenticated requests
      const { data, status } = await api.post<PasswordResetResponse>(url, payload);

      // Even if we get a 200 OK response, check if the data indicates an error
      if (status !== 200 || data.error) {
        console.warn('Password reset failed with status:', status, 'Response:', data);
        return {
          message: data.message || 'Password reset failed',
          error: data.error || `Server returned unexpected status: ${status}`,
        };
      }

      return data;
    } catch (error: any) {
      console.error('Error confirming password reset:', error);
      console.error('Error details:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message,
      });

      // Handle API errors and transform them to the expected format
      return {
        message:
          error.response?.data?.detail ||
          error.response?.data?.message ||
          'An error occurred during password reset. Please try again or request a new reset link.',
        error: 'Reset failed',
      };
    }
  },
};
